import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { DeviceNameCell } from '$components/device/name-cell';
import { RouterStatus, RouterStatusBadge } from '$components/device/router-status-badge';
import { SignalStrengthIcon } from '$components/device/signal-strength-icon';
import {
  AssociatedDevicesList,
  AssociatedDevicesCard,
} from '$features/devices/associated-devices/associated-devices-list/associated-devices-list';
import styles from '$features/devices/associated-devices/associated-devices-list/associated-devices-list.module.scss';
import {
  createDeviceQuery,
  useGetMonitorAssociations,
} from '$features/devices/devices.api';
import { AssociatedRouterModel } from '$features/devices/devices.types';

export function AssociatedRouter() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { data = [], isLoading: isSenseMonitorLoading } = useGetMonitorAssociations(
    searchParams.get('serialNumber') ?? '',
  );

  const queryResults = useQueries({
    queries: data.map((router) =>
      createDeviceQuery({
        manufacturer: 'Eppendorf',
        serialNumber: router.gatewaySerialNumber,
        enabled: !isSenseMonitorLoading,
      }),
    ),
  });

  const allQueriesCompleted =
    !isSenseMonitorLoading &&
    queryResults.length === data.length &&
    queryResults.every((result) => result.isSuccess || result.isError);

  const columnsData = useMemo(
    () =>
      data.map((availableClient, index) => {
        const queryData = queryResults.at(index)?.data;

        const router: AssociatedRouterModel = {
          serialNumber: availableClient.gatewaySerialNumber || '',
          manufacturer: queryData?.manufacturer ?? 'Eppendorf',
          model: queryData?.model ?? 'VisioNize sense gateway router',
          routerConnectionStatus:
            availableClient.registrationState === 'notWhitelisted'
              ? RouterStatus.BLOCKED
              : availableClient.connectionStatus,
          signalStrength: availableClient.signalStrength,
          name: queryData?.name ?? availableClient.gatewaySerialNumber,
          type: queryData?.type ?? 'sense-gateway',
        };
        return router;
      }),
    [queryResults, data],
  );

  return (
    <AssociatedDevicesList
      loading={isSenseMonitorLoading || !allQueriesCompleted}
      showEmptyState={
        !isSenseMonitorLoading && allQueriesCompleted && columnsData.length === 0
      }
      emptyMessage={t('deviceDetail.routerTable.emptyMessage')}
    >
      {columnsData.map((device) => (
        <AssociatedDevicesCard
          key={device.serialNumber}
          className={styles.associatedDeviceCardWithTwoRows}
        >
          <AssociatedDevicesCard.Status>
            <RouterStatusBadge status={device.routerConnectionStatus} size="medium" />
            <SignalStrengthIcon signalStrength={device.signalStrength} />
          </AssociatedDevicesCard.Status>
          <AssociatedDevicesCard.Content>
            <DeviceNameCell device={device} />
          </AssociatedDevicesCard.Content>
        </AssociatedDevicesCard>
      ))}
    </AssociatedDevicesList>
  );
}
